// ============================================================
// MyGene — Layout.tsx
// Drop-in replacement for the old top-nav layout.
// Uses the new left sidebar. All existing pages import this
// component unchanged.
// ============================================================

import React, { ReactNode } from "react";
import Sidebar, { type PatientNavProps } from "./layout/Sidebar";
import TopBar from "./layout/TopBar";
import MessagingContainer from "./messaging/MessagingContainer";
import { useSidebarCollapse } from "./layout/useSidebarCollapse";
import "./layout/Sidebar.css";

interface LayoutProps {
	children: ReactNode;
	isPatient?: boolean;
	showTour?: () => void;

	patientNav?: PatientNavProps;
}

// Patients and staff persist their collapse state under SEPARATE keys (decision D2 on
// ClickUp 86d406193). Collapsed-by-default is a patients-only change: sharing one key and
// flipping the default would collapse the sidebar for every practitioner and lab user on
// their next fresh browser profile — they were not asked, and they are not the subject of
// this ticket. Two keys also means one audience toggling the rail can never clobber the
// other's stored choice.
const PATIENT_COLLAPSE_KEY = "mg-patient-sidebar-collapsed";
const STAFF_COLLAPSE_KEY = "mg-sidebar-collapsed";

const Layout: React.FC<LayoutProps> = ({ children, isPatient, showTour, patientNav }) => {
	const storageKey = isPatient ? PATIENT_COLLAPSE_KEY : STAFF_COLLAPSE_KEY;

	const [collapsed, setCollapsed] = useSidebarCollapse(storageKey, Boolean(isPatient));

	const contentClass = ["mg-layout-content", collapsed ? "sidebar-collapsed" : ""].filter(Boolean).join(" ");

	return (
		<div style={{ position: "relative", minHeight: "100vh" }}>
			<Sidebar collapsed={collapsed} onCollapse={setCollapsed} patientNav={patientNav} />
			<TopBar collapsed={collapsed} showTour={showTour} isPatient={isPatient} />
			<main className={contentClass}>
				{children}
				<MessagingContainer />
			</main>
		</div>
	);
};

export default Layout;